/**
 * The master string list, in the manner of edge-react-gui's `en_US.ts`: this
 * file is the source of truth, and every other language is a JSON file of the
 * same keys merged over it, so a missing translation falls back to English
 * rather than showing a key.
 *
 * English is the only language for now. The framework is here so that adding
 * one later is a file, not a refactor.
 *
 * Keys read as `area_thing`, and no screen contains a literal sentence.
 */
export const enUs = {
  header_menu_close: 'Close menu',
  header_menu_panel: 'Menu',
  header_account_label: 'Account, %s',
  header_account_waiting: 'Account, %s, notifications waiting',
  header_menu_teamstation: 'TeamStation menu',
  header_account: 'Account',
  header_notifications: 'Notifications',
  header_notifications_waiting: 'Notifications waiting',
  about_title: 'About TeamStation',
  about_version: 'Version',
  about_number: 'Build number',
  about_branch: 'Branch',
  about_commit: 'Commit',
  about_time: 'Built',
  about_server: 'App server URL',
  about_builds: 'Android/iOS Beta Builds',
  about_app_store: 'iOS TestFlight',
  about_close: 'Close',
  about_close_button: 'Close build information',
  about_open_failed: 'Could not open the download site. Please try again.',
  about_test_error: 'Send a test error',
  about_test_render_error: 'Test a render error',
  about_test_native_crash: 'Test a native crash',
  error_fallback_title: 'Something went wrong',
  error_fallback_body:
    'TeamStation hit a problem it could not recover from. It has been reported.',
  error_fallback_reload: 'Reload',
  update_required_title: 'Update required',
  update_required_body:
    'This version of TeamStation can no longer follow games live. Update the app to continue.',
  update_required_reload: 'Reload',
  oauth_title: 'Authorize access',
  oauth_request: '%s wants to use your TeamStation identity.',
  oauth_identity: 'Signed in as %s',
  oauth_profile: 'Share your name',
  oauth_email: 'Share your email address and verification status',
  oauth_signin: 'Confirm your TeamStation identity',
  oauth_approve: 'Allow',
  oauth_deny: 'Deny',
  oauth_app: 'Approve in the TeamStation app',
  oauth_open_app: 'Open TeamStation',
  oauth_waiting: 'Waiting for your decision in the app…',
  oauth_done:
    'Your decision was saved. Return to the browser that requested access.',
  oauth_return: 'Return to browser',
  oauth_failed:
    'This request expired or could not be completed. Return to the requesting site and sign in again.',
  oauth_login: 'Sign in to continue',
  oauth_switch: 'Use a different account',
  // Sign in
  login_title: 'Sign in',
  login_email: 'E-mail address',
  login_password: 'Password',
  login_submit: 'Sign in',
  login_send_code: 'E-mail me a code instead',
  login_code: 'The six-digit code',
  login_code_sent: 'If that address has an account, a code is on its way.',
  login_wrong: 'That e-mail address or password is wrong.',
  login_code_wrong: 'That code is wrong or has expired.',

  // Home
  home_title: 'TeamStation',
  home_your_teams: 'Your teams',
  home_seasons: 'Seasons',
  home_nothing_yet:
    'You do not have access to anything yet. When somebody adds you to a team or a season, it will appear here.',

  // Seasons
  season_schedule: 'Schedule',
  season_teams: 'Teams',
  season_standings: 'Standings',
  season_no_games: 'No games yet.',
  season_counts: '%s teams · %s games · %s videos',
  season_no_video: 'no video',

  // Teams
  teams_title: 'Teams',
  teams_none: 'You are not on a team yet.',

  // A team's roster, by section
  roster_admins: 'Admins',
  roster_staff: 'Staff',
  roster_players: 'Players',
  roster_family: 'Family members',
  roster_spectators: 'Spectators',
  roster_followers: 'Followers',
  roster_guardian_of: 'Parent/guardian of %s',
  // The pill on somebody given their place from above: `Season coach`, and
  // for a member of the season or league, `Season spectator`.
  roster_inherited_pill: '%1$s %2$s',
  roster_level_season: 'Season',
  roster_level_league: 'League',
  roster_role_member: 'spectator',
  roster_role_admin: 'admin',
  roster_role_coach: 'coach',
  roster_role_assistant: 'assistant',
  roster_role_scorekeeper: 'scorekeeper',
  roster_role_songkeeper: 'songkeeper',
  roster_role_videographer: 'videographer',

  // Games
  games_title: 'Games',
  games_none: 'No games yet.',
  games_final: 'Final',
  games_scheduled: 'Scheduled',
  games_in_progress: 'In progress',
  games_video_count_one: '1 video',
  games_video_count_other: '%s videos',

  // One game
  game_videos: 'Videos',
  game_highlights: 'Highlights',
  video_primary: 'Video',
  video_multi_angle: 'Multi-angle',
  videos_count_one: '%s video',
  videos_count_many: '%s videos',
  games_count_one: '%s game',
  games_count_many: '%s games',
  game_box_score: 'Box score',
  /** The game page's one scoring pill, and the screen it opens. */
  game_score_game: 'Score Game',
  /** The Score action on the game hero. */
  game_hero_score: 'Score',
  /** The Record action on the game hero. It opens DJ Game. */
  game_hero_record: 'Record',
  /** The Coach action on the game hero. */
  game_hero_coach: 'Coach',
  /** The game page's pill for a head coach, and the screen it opens. */
  game_coach_game: 'Coach Game',
  /** Asked of a head coach of both teams before Coach Game opens. */
  game_coach_which: 'Coach which team?',
  /** Asked before a coach leaves Coach Game with changes not saved. */
  coach_discard_title: 'Discard unsaved roster changes?',
  coach_discard: 'Discard',
  coach_keep_editing: 'Keep editing',
  coach_not_coach: 'Only a head coach of this team can coach it here.',
  coach_load_failed: 'Unable to load the game. Please try again.',
  coach_save_failed: 'Unable to save the lineup. Please try again.',
  game_replay: 'Replay',
  game_live: 'Live',
  replay_exit: 'Exit',
  replay_announcer_on: 'Turn announcer on',
  replay_announcer_off: 'Turn announcer off',
  replay_pause: 'Pause replay',
  replay_play: 'Play replay',
  replay_paused: 'Paused',
  replay_rewind: 'Rewind half an inning',
  replay_rewind_top: 'Rewind to top of %s',
  replay_rewind_bottom: 'Rewind to bottom of %s',
  replay_play_ball: 'Play ball!',
  replay_waiting: 'Waiting for the next pitch',
  replay_play_changed: 'Play changed by the scorer',
  replay_announcer_unavailable: 'Announcer unavailable',
  /** The title of the simplified scoreboard, the 1.0 override dashboard. */
  game_override_scoreboard: 'Override Scoreboard',
  /** The Videos tab of a game nothing was recorded of, in place of a player. */
  game_no_video: 'No videos.',
  game_video_unavailable: 'This video is not available right now.',
  game_video_open: 'Watch full screen',
  /** Marks a video in the picker whose file is no longer on the server. */
  game_video_gone_suffix: '(unavailable)',
  /**
   * Where somebody signed in who is not a member meets what is kept for
   * members on a team page: the batting and pitching, and the team's videos.
   * The server's own `membersOnly` words.
   */
  members_only: 'You need to be a member of one of the teams to see this.',
  /**
   * A game's Videos tab for a non-member, in place of a player, and again
   * on tapping one of its highlights.
   */
  game_videos_members_only:
    "You need to be a member of one of the teams to watch this game's videos.",
  /** Under a non-member's line score, in place of each side's lines. */
  box_score_members_only:
    'You need to be a member of one of these teams to see individual player stats.',
  /** Under a coach's empty line score, before the game has a line score. */
  box_score_not_started: 'The game has not started',
  box_score_totals: 'Totals',

  // Four angles at once
  angles_open: 'All angles',
  angles_unavailable:
    'Multi-camera viewing is unavailable in this browser. Please switch to Brave or Chrome on desktop or Android, or the native TeamStation mobile app.',
  angles_unavailable_ok: 'OK',
  angles_title: 'Choose up to four angles',
  angles_detail:
    'They play together, on one clock. Hold a picture for a second, then drag it to move it.',
  angles_cap: 'Four at a time is the most the picture holds.',
  angles_watch: 'Watch',
  angles_column_angle: 'Angle',
  angles_column_show: 'Show',
  angles_column_sound: 'Sound',
  angles_sound_from_label: 'Hear %s',
  angles_cancel: 'Cancel',
  angles_swipe_full_screen: 'Swipe up for full screen',
  angles_not_recording_yet: 'Not recording yet',
  angles_stopped: 'Stopped recording',
  angles_all_ended: 'Every angle has ended.',
  angles_unplaceable:
    'This recording has no start time, so it cannot be held in step with the others.',
  angles_waiting: 'Waiting for the pictures to catch up…',
  angles_tap_for_sound: 'Tap for sound',
  angles_tile_loading: 'Camera loading…',
  angles_tile_catching_up: 'Camera catching up…',
  angles_tile_label: '%s angle',
  angles_tile_waiting_live: 'Waiting for this camera to catch up to the game…',
  video_not_supported:
    "This browser can't play TeamStation video. Try Chrome, Edge or Safari.",
  angles_close: 'Close',
  angles_swipe_to_close: 'Swipe down to close',
  angles_play: 'Play',
  angles_pause: 'Pause',
  angles_back_ten: 'Back ten seconds',
  angles_forward_ten: 'Forward ten seconds',
  player_slow_mo: 'Slow Mo 25%',
  game_batting: 'Batting',
  game_box_score_empty: 'No batting to show for this game yet.',
  game_at: 'at',
  game_score_mismatch:
    'The plays do not add up to the final score, so highlights are held back until somebody has checked them.',

  // Highlights
  highlight_jump: 'Jump to this play',
  highlights_none: 'No highlights for this video.',
  highlights_none_named:
    'No highlights we can name for this video. The plays are there, but the players on them are not in our records.',
  /**
   * Some games were scored from the recording afterwards, sometimes days
   * later. The plays are real and the box score is right; the tap times just
   * say when somebody sat down with the video, so nothing can place them on
   * it. Said plainly, because an empty list with no reason reads as a fault.
   */
  highlights_scored_after:
    'This game was scored after the video was recorded, so the plays cannot be placed on it. The box score and stats are still complete.',
  highlights_no_play_times:
    'This game\u2019s plays have no times recorded, so they cannot be placed on the video.',
  highlights_no_video_start:
    'This video has no start time recorded, so plays cannot be placed on it.',

  // One player
  player_videos: 'Videos',
  player_stats: 'Stats',
  player_sections: 'Player sections',
  player_cannot_load: 'Cannot load this player.',
  player_unavailable: 'Profile not available',
  player_unavailable_body:
    'You don\u2019t have access to this player\u2019s profile, or it no longer exists.',
  player_videos_empty: 'No highlights of this player yet.',
  player_videos_none_recorded:
    'None of this player\u2019s games were recorded, so there is nothing to watch yet.',
  player_loading: 'Loading\u2026',
  player_stats_empty: 'No stats for this player yet.',
  player_batting: 'Batting',
  player_pitching: 'Pitching',
  player_no_batting: 'This player has never batted in a game we hold.',
  player_stats_total: 'Total',
  player_stats_team: 'Team',
  player_stats_game: 'Game',
  player_no_pitching: 'This player has never pitched in a game we hold.',
  player_filter: 'Filter',
  player_filter_empty: 'No games under this filter.',
  player_filter_clear: 'Show every game',
  player_filter_title: 'Which games?',
  player_filter_career: 'Career',
  player_filter_career_detail: 'Every game, every team, every season.',
  player_filter_by_season: 'By season',
  player_filter_by_team: 'By team',
  player_filter_by_year: 'By year',
  player_filter_apply: 'Show these',
  player_filter_cancel: 'Cancel',
  /** `12 games · Spring 2026` under the heading. */
  player_stats_scope: '%s \u00b7 %s',
  /** What a filter narrows to when the season or team is no longer known. */
  player_filter_unknown: 'a filter that no longer exists',
  player_games: '%s games',
  player_one_game: '1 game',
  /** The pill above a player's videos that opens the filter. */
  player_videos_filter: 'Filter videos',
  player_videos_filter_empty: 'No highlights under this filter.',

  // Every team a player has played for
  player_teams: 'Teams',
  player_teams_empty: 'Not on any team yet.',

  // A player's settings: on the team the page was opened for, their jersey
  // and positions; away from any team, how they bat and throw
  player_settings: 'Settings',
  player_settings_first_name: 'First name',
  player_settings_last_name: 'Last name',
  player_settings_jersey: 'Jersey number',
  player_settings_positions: 'Positions',
  player_settings_bats: 'Bats',
  player_settings_throws: 'Throws',
  player_settings_right: 'Right',
  player_settings_left: 'Left',
  player_settings_switch: 'Switch',
  player_settings_save: 'Save',
  player_settings_saved: 'Saved.',
  player_settings_cannot_load: 'Cannot load this player\u2019s settings.',
  player_settings_cannot_save: 'Cannot save these settings.',
  player_settings_not_on_team: 'This player is not on this team\u2019s roster.',
  player_form_positions: 'Select player positions',
  player_birth: 'Birth month',
  player_birth_detail:
    'The month and year only, never the day. A birthday counts from the first of the month after.',
  player_birth_month: 'Month',
  player_birth_year: 'Year',
  player_birth_year_earlier: 'One year earlier',
  player_birth_year_later: 'One year later',
  player_birth_none: 'Not set',
  player_birth_shown: 'Born %s %s \u00b7 %s today',
  player_birth_save: 'Save birth month',
  player_birth_clear: 'Clear birth month',
  player_birth_saved: 'Birth month saved.',
  player_birth_cleared: 'Birth month cleared.',
  month_1: 'January',
  month_2: 'February',
  month_3: 'March',
  month_4: 'April',
  month_5: 'May',
  month_6: 'June',
  month_7: 'July',
  month_8: 'August',
  month_9: 'September',
  month_10: 'October',
  month_11: 'November',
  month_12: 'December',

  // A player's guardians and adult family members
  guardians_title: 'Guardians and family',
  guardians_title_for: 'Guardians and family of %s',
  guardians_detail:
    'A guardian joins the team through this link. An adult family member gets no access; they only count for who may message the player.',
  guardians_open: 'Guardians and family',
  guardians_manage: 'Change guardians and family',
  guardians_none: 'Nobody is linked yet.',
  guardians_kind_guardian: 'Guardian',
  guardians_kind_family: 'Adult family member',
  guardians_kind_family_short: 'Family',
  guardians_remove: 'Remove',
  guardians_remove_label: 'Remove %s',
  guardians_add: 'Add somebody',
  guardians_add_field: 'E-mail address or name',
  guardians_add_placeholder: 'parent@example.com, or a name on the team',
  guardians_add_submit: 'Add by e-mail',
  guardians_pick: 'Add %s',
  guardians_added: '%s is linked.',
  guardians_invited:
    'An invitation is on its way to %s. Only that address can accept it.',
  guardians_needs_account:
    'That person has no way to sign in. Add them by e-mail address instead.',
  guardians_cannot_load: 'Cannot load the guardians.',
  guardians_done: 'Done',

  // Guardians: a guardian, or a team's coaching staff while the player has
  // none, invite one by address from Settings; anybody else who can see the
  // player asks to be one under their name
  player_guardian_invite: 'Invite a guardian',
  player_guardian_invite_detail:
    'A guardian sees every team %s plays on and manages their number, positions and walk-up song. They accept from their notifications.',
  player_guardian_email: 'E-mail address',
  player_guardian_send: 'Send invitation',
  player_guardian_cannot_send: 'The invitation could not be sent. Retry.',
  player_guardian_request: 'Request to be a guardian',
  player_guardian_request_title: 'Ask to be %s\u2019s guardian?',
  player_guardian_request_send: 'Send request',
  player_guardian_request_detail:
    '%s or a guardian of theirs decides, or a coach of one of their teams if they have no guardian. Once approved you see their teams and manage their details.',
  player_guardian_request_sent: 'Guardian request sent',
  player_guardian_cannot_request: 'The request could not be sent. Retry.',

  // A player's walk-up song, for a songkeeper of the team the page was opened for
  player_walkup: 'Walk-up',
  player_walkup_none: 'No walk-up song yet',
  player_walkup_web_note:
    'Set walk-up songs in the TeamStation app on iPhone or Android.',
  player_walkup_apple_music: 'Apple Music',
  player_walkup_local_file: 'A file on the phone',
  player_walkup_clip: 'Plays %s',
  player_walkup_set: 'Set',
  player_walkup_change: 'Change',
  player_walkup_preview: 'Preview',
  player_walkup_stop: 'Stop',
  player_walkup_not_here: 'Not on this phone',
  player_walkup_remove: 'Remove',
  player_walkup_remove_confirm: 'Tap again to remove',
  player_walkup_cannot_load: 'Cannot load this player\u2019s walk-up song.',
  player_walkup_cannot_remove: 'Cannot remove this walk-up song.',

  // The song picker, on a phone: search, then the clip
  walkup_title: 'Walk-up song for %s',
  walkup_search: 'Search',
  walkup_search_apple: 'Apple Music and your library',
  walkup_search_device: 'Music on this phone',
  walkup_licence:
    'Playing music at a field may need the league’s performance licence.',
  walkup_choose_file: 'Choose a file…',
  walkup_searching: 'Searching…',
  walkup_no_match: 'No songs match',
  walkup_retry: 'Retry',
  paged_list_load_failed: 'Could not load more',
  paged_list_retry: 'Retry',
  walkup_badge_catalog: 'Apple Music',
  walkup_badge_library: 'Library',
  walkup_badge_device: 'On this phone',
  walkup_checking: 'Checking music access…',
  walkup_denied: 'TeamStation can’t see your music',
  walkup_denied_ios:
    'Allow TeamStation to use Apple Music and your media library in Settings, then come back here.',
  walkup_denied_android:
    'Allow TeamStation to read the music on this phone in Settings, then come back here.',
  walkup_restricted: 'Music is restricted on this phone.',
  walkup_unavailable: 'Music isn’t available on this phone.',
  walkup_open_settings: 'Open Settings',
  walkup_pick_another: 'Pick another song',
  walkup_play: 'Play',
  walkup_start: 'Start',
  walkup_end: 'End',
  walkup_jog: 'Fine adjust: drag to move the clip slowly',
  walkup_setup_intro:
    'For the best and most reliable experience with TeamStation DJ, enable the following settings in your Apple Music app.',
  walkup_setup_add_playlist: 'Add Playlist Songs',
  walkup_setup_sync: 'Sync Library',
  walkup_setup_cellular: 'Download over Cellular',
  walkup_setup_auto: 'Automatic Downloads',
  walkup_setup_path: 'Settings › Apps › Music',
  walkup_setup_note:
    'Songs that were already in your library don’t download by themselves. Open the TeamStation walkup songs playlist in Music and tap Download.',
  walkup_setup_next: 'Next',
  walkup_setup_back: 'Back',
  walkup_setup_done: 'Done',
  walkup_setup_settings: 'Go to Settings',
  walkup_setup_close: 'Close',
  walkup_not_downloaded:
    'Some walk-up songs still need to be downloaded. Make sure your Apple Music settings are configured properly.',
  walkup_not_downloaded_settings: 'Go to Settings',
  walkup_not_downloaded_ignore: 'Ignore',
  walkup_length: '%s s',
  walkup_earlier: '1 s earlier',
  walkup_later: '1 s later',
  walkup_play_from_here: 'Play from here',
  walkup_save: 'Save',
  walkup_cannot_save: 'Cannot save this walk-up song.',

  // The Account screen, on an iPhone
  dj_game: 'DJ Game',
  dj_matchup: '%s @ %s',
  dj_note_default: 'Lineup not set for this game — showing the team’s default',
  dj_note_roster: 'No lineup yet — showing the roster',
  dj_at_bat: 'at bat',
  dj_no_song: 'No walk-up song',
  dj_song: '%s · %s',
  dj_not_here: '%s isn’t on this phone',
  dj_play: 'Play %s’s walk-up song',
  dj_stop: 'Stop %s’s walk-up song',
  dj_edit: 'Set %s’s walk-up song',
  dj_empty: 'No batters to list',
  dj_cannot_load: 'Cannot load this game’s lineups.',
  dj_retry: 'Retry',
  // The scoring screen's music note, for the batter coming up
  walkup_note_play: 'Play %s’s walk-up song',
  walkup_note_stop: 'Stop %s’s walk-up song',
  walkup_note_not_here: 'Walk-up song for %s, not on this phone',
  account_walkup: 'Walk-up songs',
  account_walkup_apple_music: 'Apple Music settings',
  account_walkup_fade: 'Fade Apple Music using the phone’s volume',
  account_walkup_fade_detail:
    'Some Bluetooth speakers beep when the volume changes. Turn this off for hard starts and stops.',
  account_delete: 'Delete account',
  account_delete_confirm: 'Are you sure? Deleting an account is irrecoverable.',
  account_delete_slide: 'Slide to delete',
  account_delete_cancel: 'Cancel',
  account_delete_failed: 'Your account could not be deleted.',
  account_delete_session_ended:
    'Your session ended. Sign in again to finish deleting your account.',
  account_delete_sign_in: 'Sign in',
  account_chat: 'Chat',
  account_chat_devices: 'Chat devices',
  account_chat_devices_detail: 'The devices that can read your chats',
  account_sign_out: 'Sign out',
  account_api_keys: 'API keys',
  account_api_keys_load_error: 'Could not load your API keys',
  account_api_key_create: 'Create API key',
  account_api_key_copy: 'Copy %s',
  account_api_key_copied: 'Copied the secret of %s',
  account_api_key_delete: 'Delete',
  account_api_key_delete_named: 'Delete %s',
  account_api_key_delete_title: 'Delete %s?',
  account_api_key_delete_detail: 'Anything using this key stops working.',
  api_key_new_name: 'Name',
  api_key_new_read: 'R',
  api_key_new_write: 'W',
  api_key_new_read_label: '%s read',
  api_key_new_write_label: '%s write',
  api_key_new_create: 'Create API key',

  // A DM's name once everybody else has left it
  chat_dm_left: '%s (left)',
  chat_dm_just_you: 'Just you',

  // Chat devices, linking and reset
  chat_devices_title: 'Chat devices',
  chat_devices_intro:
    'Every device here can read your chats. A new device joins only when one of these approves it.',
  chat_devices_list: 'Your devices',
  chat_devices_this: '%s (this device)',
  chat_devices_linked: 'Linked',
  chat_devices_pending: 'Not linked yet',
  chat_devices_platform_web: 'Web',
  chat_devices_platform_ios: 'iPhone or iPad',
  chat_devices_platform_android: 'Android',
  chat_devices_remove: 'Remove',
  chat_devices_remove_label: 'Remove %s',
  chat_devices_requests: 'Waiting for approval',
  chat_devices_request: '%s asks to read your chats',
  chat_devices_request_detail:
    'Approve only if %s shows these same six digits.',
  chat_devices_code_label: 'Code %s',
  chat_devices_approve: 'Approve',
  chat_devices_approve_label: 'Approve %s',
  chat_devices_decline: 'Decline',
  chat_devices_decline_label: 'Decline %s',
  chat_devices_cannot_load: 'Chat devices could not be loaded.',
  chat_devices_failed: 'That did not work. Try again.',
  chat_link_title: 'Link this device',
  chat_link_detail:
    'Open Chat devices on a device where you already use chat, and approve this one there when it shows the same six digits.',
  chat_link_code_label: 'Six-digit code %s',
  chat_link_waiting: 'Waiting for approval…',
  chat_link_expired: 'That request expired.',
  chat_link_declined: 'That request was declined. You can ask again.',
  chat_approval_on_linked_device:
    'Open this request on a device already approved for chat.',
  chat_approval_later: 'Later',
  chat_link_again: 'Ask again',
  chat_link_cant_reach: 'I can’t reach my other devices',
  chat_link_done: 'This device is linked.',
  chat_link_open_devices: 'Chat devices',
  chat_reset_title: 'Start chat again on this device',
  chat_reset_no_device:
    'None of your devices can approve this one, so chat starts again here.',
  chat_reset_detail:
    'Your other devices will stop working for chat. Earlier messages stay locked until another member of each chat opens TeamStation and unlocks them for you, and the people you chat with will see that your safety words changed.',
  chat_reset_submit: 'Reset chat on this device',
  chat_reset_back: 'Back to linking',

  // The chat screens
  chat_title: 'Chat',
  chat_chats: 'Chats',
  chat_information: 'Conversation information',
  chat_direct_conversation: 'Direct message',
  chat_pinned: 'Pinned',
  chat_choose_conversation: 'Choose a conversation',
  chat_conversation_actions: 'Conversation actions',
  chat_mark_unread: 'Mark unread',
  chat_mute: 'Mute',
  chat_unmute: 'Unmute',
  chat_pin: 'Pin',
  chat_unpin: 'Unpin',
  chat_muted: 'Muted',
  chat_preference_failed: 'Could not update this conversation. Try again.',
  chat_message_unavailable: 'Message unavailable',
  chat_photo_label: 'Photo',
  chat_video_label: 'Video',
  chat_attachments_label: 'attachments',
  chat_latest_loading: 'Loading latest message…',
  chat_latest_failed: 'Could not load latest message.',
  chat_preview_retry: 'Try loading latest message again',
  chat_manual_unread_label: 'Marked unread',
  chat_new_message_announcement: 'New message in %s',
  chat_new_message: 'New message',
  chat_loading: 'Opening chat…',
  chat_cannot_load: 'Chat could not be opened.',
  chat_keys_unreadable:
    'The chat keys saved on this device cannot be read. Resetting chat on this device removes them, then sets this device up for chat again.',
  chat_keys_newer:
    'A newer version of TeamStation saved the chat keys on this device. Update the app to open chat.',
  chat_core_needs_webassembly: 'Chat needs WebAssembly',
  chat_core_no_webassembly:
    'This browser has WebAssembly turned off, or does not have it. On an iPhone, iPad or Mac, Lockdown Mode turns it off in Safari.',
  chat_core_module_failed:
    'This browser could not load chat’s encryption. Reload the page to try again.',
  chat_core_not_in_build: 'Chat is not available in this build',
  chat_core_rest_works: 'Everything else in TeamStation works as usual.',
  chat_empty: 'You are in no chats yet.',
  chat_empty_detail:
    'Your team, season, league and direct conversations appear here.',
  chat_direct: 'Direct messages',
  chat_unread_label: '%s unread',
  chat_locked_badge: 'Locked',
  chat_closed_badge: 'Closed',
  chat_no_messages: 'No messages yet',
  chat_history_locked:
    'History is locked — it unlocks when another member opens TeamStation',
  chat_earlier_lost: 'Earlier messages can’t be opened',
  chat_unverified: 'Membership record could not be verified',
  chat_closed_notice: 'This chat is closed. You can read it, but not post.',
  chat_read_only: 'Only season staff post here. You can react.',
  chat_load_earlier: 'Load earlier messages',
  chat_composer: 'Message',
  chat_composer_locked: 'Waiting for history to unlock…',
  chat_send: 'Send',
  chat_attach: 'Attach',
  chat_emoji: 'Emoji',
  chat_photo_library: 'Photo library',
  chat_file: 'File',
  chat_cancel: 'Cancel',
  chat_remove_attachment: 'Remove %s',
  chat_replying_to: 'Replying to %s',
  chat_editing: 'Editing your message',
  chat_cancel_reply: 'Cancel reply',
  chat_cancel_edit: 'Cancel edit',
  chat_former_member: 'Former member',
  chat_you: 'You',
  chat_edited: 'edited',
  chat_removed_author: 'Message deleted',
  chat_removed_moderator: 'Removed by a moderator',
  chat_locked_message: 'Locked message',
  chat_lost_message: 'This message can’t be opened',
  chat_invalid_message: 'This message could not be verified',
  chat_sent_before_change: 'sent before %s’s safety words changed',
  chat_safety_changed: '%s’s safety words changed',
  chat_read_by: 'Read by %s',
  chat_seen_by: 'Seen by %s',
  chat_sent: 'Sent',
  chat_typing_one: '%s is typing…',
  chat_typing_many: '%s are typing…',
  chat_someone: 'Someone',
  chat_message_actions: 'Message from %s',
  chat_reply: 'Reply',
  chat_react: 'React with %s',
  chat_more_emoji: 'More emoji',
  chat_edit: 'Edit',
  chat_delete: 'Delete',
  chat_copy: 'Copy',
  chat_copy_text: 'Copy text',
  chat_copy_failed: 'Could not copy this message.',
  chat_pin_failed: 'Could not update this message pin. Try again.',
  chat_attachment_limit: 'A message can include up to 10 attachments.',
  chat_attaching_file: 'Attaching file to chat',
  chat_attachment_blocked: 'Attachments are unavailable in this conversation.',
  chat_send_emoji: 'Send an emoji',
  chat_more_message_actions: 'More message actions',
  chat_latest: 'Go to latest message',
  chat_attention_loading: 'Checking mentions and replies…',
  chat_attention_retry: 'Retry checking mentions and replies',
  chat_attention_count: '%s unread mentions and replies below',
  chat_mention_title: 'Mention a member',
  chat_mention_search: 'Search members',
  chat_mention_none: 'No members found.',
  chat_mention_too_long: 'This mention would make the message too long.',
  chat_pinned_messages: 'Pinned messages',
  chat_no_pinned_messages: 'No pinned messages.',
  chat_message_pinned: 'Pinned message',
  chat_delete_confirm: 'This message will be removed for everyone.',
  chat_jump_reply: 'Jump to message from %s: %s',
  chat_jump_earlier_reply: 'Jump to the earlier message this replies to',
  chat_quote_earlier: 'Earlier message',
  chat_quote_show: 'Tap to show it',
  chat_pin_earlier: 'Earlier message. Tap to show it.',
  chat_delete_for_everyone: 'Delete for everyone',
  chat_age_title: 'Before You Chat',
  chat_age_body:
    'Chat on TeamStation is for people 13 and older. Players under 13 can still follow their teams, schedules, stats and videos.',
  chat_age_rules:
    'By continuing, you confirm that you are at least 13 years old and agree to keep chat respectful: no bullying, harassment, hate speech, threats, or sexual or violent content. You can mute any chat and report any message to TeamStation’s site administrators, who review what is reported.',
  chat_age_agree: 'I’m 13 or Older',
  chat_age_decline: 'Not Now',
  chat_age_under13_title: 'Chat Is for Ages 13 and Up',
  chat_age_under13_body:
    'The age range this device shares says you’re under 13, so chat isn’t available here. You can still follow your teams, schedules, stats and videos.',
  chat_age_under13_ok: 'OK',
  chat_age_failed: 'That didn’t save. Check your connection and try again.',
  chat_report: 'Report',
  chat_report_title: 'Report this message?',
  chat_report_detail:
    'TeamStation’s site administrators will be able to read this one message from %s, to decide what to do about it. Nothing else in this chat is shared with them.',
  chat_report_send: 'Report message',
  chat_reported:
    'Reported. TeamStation’s site administrators can now read that message.',
  chat_report_failed: 'That message could not be reported. Try again.',
  chat_reports_title: 'Reported messages',
  account_chat_reports_detail:
    'Chat messages people reported, for site administrators',
  chat_reports_open: 'Open',
  chat_reports_all: 'All',
  chat_reports_none: 'Nothing has been reported.',
  chat_reports_none_open: 'No open reports.',
  chat_reports_verified: 'verified',
  chat_reports_unverified: 'not verified',
  chat_reports_verified_detail:
    'The sender’s signature and the disclosed key prove they wrote this.',
  chat_reports_unverified_detail:
    'The disclosed text or key did not match the message: treat it as unproven.',
  chat_reports_from: 'From %s in %s',
  chat_reports_reported_by: 'Reported by %s · %s',
  chat_reports_resolved: 'Resolved %s',
  chat_reports_resolve: 'Resolve',
  chat_reports_failed: 'The reports could not be loaded.',
  chat_reports_resolve_failed: 'That report could not be resolved. Try again.',
  chat_reports_not_found: 'There is nothing here.',
  chat_reports_attachments_one: '%s attachment',
  chat_reports_attachments_many: '%s attachments',
  chat_reports_kind_dm: 'a direct message',
  chat_reaction_label: '%s, %s',
  chat_open_attachment: 'Open %s',
  chat_save_file: 'Save %s',
  chat_attachment_failed: 'That attachment could not be opened.',
  chat_send_failed: 'That did not send. Try again.',
  chat_too_large: '%s is too large: files can be at most 100 MB.',
  chat_members: 'Members',
  chat_members_count_one: '%s member',
  chat_members_count_many: '%s members',
  chat_safety_number_with: 'Safety words with %s',
  chat_safety_number_detail:
    'Compare these 16 words with %s, side by side or read aloud. If they match, nobody is between you.',
  chat_safety_number_none: '%s has not set up chat yet.',
  chat_approvals: 'Waiting for approval',
  chat_approval_request:
    '%s reset their chat keys and asks for this chat’s history.',
  chat_old_safety_number: 'Old safety words',
  chat_new_safety_number: 'New safety words',
  chat_approve: 'Approve',
  chat_review: 'Review',
  chat_approve_label: 'Approve %s',
  chat_approve_failed: 'That did not work. Try again.',
  chat_leave_dm: 'Leave this conversation',
  chat_moderator: 'Moderator',
  chat_new_title: 'New message',
  chat_new_search: 'Name',
  chat_new_none: 'Nobody matches.',
  chat_new_search_failed: 'People could not be loaded.',
  chat_new_pick: 'Pick %s',
  chat_new_unpick: 'Remove %s',
  chat_new_start: 'Start conversation',
  chat_new_not_allowed: 'You cannot start a conversation with %s.',
  chat_new_failed: 'The conversation could not be started.',
  chat_new_too_many: 'A conversation holds at most 20 people.',
  chat_include_title: 'Include another adult',
  chat_include_detail:
    'This league is a SafeSport Local Affiliated Organization. Messages between an adult and a player under 18 must also include the player’s parent or guardian, an adult family member, or another adult of the team, season or league. Pick at least one.',
  chat_include_search: 'Search for an adult',
  chat_include_suggestions: 'Suggested',
  chat_include_results: 'Matches',
  chat_include_none: 'Nobody matches that name.',
  chat_include_pick: 'Include %s',
  chat_include_unpick: 'Do not include %s',
  chat_include_submit: 'Start with them',
  chat_team_chats: 'Chats',
  chat_open_chat: 'Open %s',

  // Box score and stats
  stats_title: 'Stats',
  game_pitching: 'Pitching',
  stats_player: 'Player',
  stats_season: 'Season',
  stats_career: 'Career',
  stats_plate_appearances: 'PA',
  stats_at_bats: 'AB',
  stats_hits: 'H',
  stats_walks: 'BB',
  stats_strikeouts: 'K',
  stats_runs_batted_in: 'RBI',
  stats_average: 'AVG',
  stats_on_base: 'OBP',
  stats_slugging: 'SLG',
  stats_no_average: '—',
  stats_runs: 'R',
  stats_earned_runs: 'ER',
  stats_era: 'ERA',
  stats_whip: 'WHIP',
  stats_innings: 'IP',
  stats_games: 'G',
  stats_stolen_bases: 'SB',
  stats_home_runs: 'HR',
  stats_ops: 'OPS',
  stats_show_plays: 'Show the plays behind this',

  // Standings
  standings_title: 'Standings',
  standings_wins: 'W',
  standings_losses: 'L',
  standings_ties: 'T',
  standings_runs_for: 'RF',
  standings_runs_against: 'RA',

  // Invites
  invite_title: 'Invite Spectators',
  invite_detail:
    'Anybody with the link can watch %s. They will be asked for their name and e-mail address.',
  invite_none: 'No invite links yet.',
  invite_create: 'Create an invite link',
  invite_code_label: 'Code (optional)',
  invite_code_placeholder: 'fall26',
  invite_label_label: 'What it is for (optional)',
  invite_label_placeholder: 'Parents',
  invite_submit: 'Create',
  invite_cancel: 'Cancel',
  invite_copy: 'Copy link',
  invite_copied: 'Copied',
  invite_revoke: 'Disable',
  invite_uses_none: 'Never used',
  invite_uses_one: 'Used once',
  invite_uses_many: 'Used %s times',
  invite_revoked: 'Stopped',
  invite_expired: 'Expired',
  invite_exhausted: 'All used up',

  // A group's people, in its Settings
  manage_remove: 'Remove',
  manage_add: 'Add somebody',
  manage_add_detail:
    'Add someone to %s by email. Somebody with an account is added at once, and told; anybody else is sent an invitation, which applies when they sign up with that address. Invitations last seven days.',
  manage_email: 'E-mail address',
  manage_add_submit: 'Add',
  manage_choose_roles: 'Choose roles',
  manage_add_as: 'Adds them as: %s.',
  manage_new_person: 'the person you add',
  manage_added: '%s has been added.',
  manage_invited: 'An invitation is on its way to %s.',
  manage_already: '%s already had access.',
  manage_open_sent: 'Open sent invitation',
  manage_pending_header: 'Invitations waiting',
  manage_invited_on: 'Invited %s, not joined yet',
  manage_open_invitation: 'Open',
  manage_no_address_header: 'No address on file',
  manage_no_address_detail:
    'Nobody can sign in to these accounts, so they hold no role. Remove one to take it out of here.',
  manage_open: 'Manage',

  // Changing what somebody may do at one level
  role_title: 'Roles for %s',
  role_scope: 'At %s, and everything inside it.',
  role_team_admin: 'Team admin',
  role_season_admin: 'Season admin',
  role_league_admin: 'League admin',
  role_admin_detail:
    'Can invite people to %s, remove them, and change what everybody here is.',
  role_head_coach: 'Manager/Head Coach',
  role_coach: 'Coach',
  role_coach_detail:
    'Edits the players\u2019 jerseys, positions, bats and throws in %s, and sees their full names.',
  role_assistant: 'Assistant',
  role_assistant_detail:
    'Same as a coach: edits the players\u2019 jerseys, positions, bats and throws in %s, and sees their full names.',
  role_scorekeeper: 'Scorekeeper',
  role_scorekeeper_detail:
    'Scores the games of %s, sets walk-up songs and DJs them.',
  role_songkeeper: 'Songkeeper',
  role_songkeeper_detail: 'Sets walk-up songs and DJs the games of %s.',
  role_videographer: 'Videographer',
  role_videographer_detail: 'Films and streams the games of %s.',
  role_from: 'From %s',
  role_admin_all: 'Admins can do all of this',
  role_scorekeeper_all: 'Scorekeepers can do this',
  role_save: 'Save',
  role_cancel: 'Cancel',

  // A person's profile, opened from a team's, season's or league's people
  profile_context_team: 'Team \u00b7 %s',
  profile_context_season: 'Season \u00b7 %s',
  profile_context_league: 'League \u00b7 %s',
  profile_guardian_of: 'Parent/Guardian of',
  profile_team_roles: 'Team roles',
  profile_season_roles: 'Season roles',
  profile_league_roles: 'League roles',
  profile_level_note: 'Change these on %s\u2019s page.',
  profile_no_sign_in:
    '%s can\u2019t sign in, so can be a member here but hold no role.',
  profile_admin: 'Admin',
  profile_member: 'Member',
  profile_follower: 'Follower',
  profile_held: 'Yes',
  profile_not_held: '\u2014',
  profile_with: 'With %s',
  profile_player: 'Player',
  profile_following: 'Following',
  profile_not_following: 'Not following',
  profile_refused: 'You can\u2019t open this person here.',
  player_team_roles: 'Team roles',
  player_remove_from_team: 'Remove from %s',
  player_remove_title: 'Remove %1$s from %2$s?',
  player_remove_confirm: 'Remove %s',
  player_remove_detail:
    '%1$s leaves %2$s: off the roster, and every role they hold on it.',
  player_removed: 'Removed %1$s from %2$s',
  player_remove_unchanged: '%1$s is not on %2$s. Nothing was changed.',
  people_none: 'Nobody here yet.',
  manage_guardians: 'Guardians',
  manage_safesport_title: 'SafeSport',
  manage_safesport_label: 'SafeSport Local Affiliated Organization',
  manage_safesport_detail:
    "This league is directly affiliated with USA Baseball or one of its state or regional affiliates, which makes it a SafeSport Local Affiliated Organization. The MAAPP then binds it: an adult's one-on-one messages to a player under 18 must also include the player's parent or guardian, an adult family member, or another adult of the league. A league that is only a chartered Little League is not one.",

  // Coming in through an invite
  join_header: 'Invitation',
  join_loading: 'Checking that invite…',
  join_unknown: 'Invalid invite link',
  join_unknown_detail:
    'The link may have expired or been revoked, or its address may be incomplete.',
  join_dead: 'Invalid invite link',
  join_title: 'You have been invited',
  join_scope_team: 'You are invited to watch %s',
  join_scope_season: 'You are invited to watch the whole %s season',
  join_scope_league: 'You are invited to watch everything in %s',
  join_name: 'Your name',
  join_name_placeholder: 'Alex Rivera',
  join_email: 'E-mail address',
  join_email_placeholder: 'parent@example.com',
  join_send_code: 'Send me a sign-in code',
  join_code: 'Sign-in code',
  join_code_sent:
    'A six-digit code is on its way. It lasts ten minutes, and the link in the same message works too.',
  join_finish: 'Join',
  join_accept: 'Accept the invitation',
  join_already: 'You can already watch %s.',
  join_welcome: 'You are in. %s is on your home screen.',
  join_open: 'Have a look',
  join_for_another_email:
    'This invitation was sent to another e-mail address. Sign in with that address to accept it.',

  // Scoring control presentation (engine action identifiers stay stable)
  scoring_control_more: 'MORE',
  scoring_control_song: 'SONG',
  scoring_control_stop: 'STOP',
  scoring_control_ball: 'BALL',
  scoring_control_called_strike: 'CALLED\nSTRIKE',
  scoring_control_swing_miss: 'SWING\n& MISS',
  scoring_control_foul: 'FOUL\nBALL',
  scoring_control_bunt: 'BUNT',
  scoring_control_swing: 'SWING',
  scoring_control_undo: 'UNDO',
  scoring_in_play: 'IN PLAY',
  scoring_out_zone: 'OUT ZONE',
  scoring_no_plays: 'No plays yet',
  scoring_history_hint: 'Your last action appears here',
  scoring_undo_hint: 'Undo last action',
  scoring_place_runner: 'Place a Runner',
  scoring_roster_tab: 'Roster',
  scoring_edit_roster_instruction: 'Tap player to edit.',
  scoring_roster_empty: 'No players on this team.',
  scoring_roster_conflict:
    'Another user has edited the roster. Your pending changes may overwrite theirs. Hit Cancel to revert to their changes.',
  scoring_game_only_player: 'Game-only player',
  scoring_roster_edit_unavailable:
    'Player settings are unavailable for this team.',
  scoring_tiebreaker: '(Tiebreaker)',
  scoring_out_type: 'OUT TYPE',
  scoring_submenu: 'SUB-MENU',
  scoring_at_home: 'At Home',
  scoring_at_first: 'At First',
  scoring_at_second: 'At Second',
  scoring_at_third: 'At Third',
  scoring_bottom_action: 'Bottom %s',
  scoring_finish_at_bat: 'Finish at-bat',
  scoring_finish_defensive_play: 'Finish defensive play',

  // A game's scoreboard page, once it has closed
  scoreboard_not_live: "This scoreboard isn't live",
  scoreboard_not_live_detail:
    'Scoreboards open when a game is scheduled and close after it ends.',
  scoreboard_sign_up: 'Sign up for TeamStation',

  // The Score Game screen
  score_game_full_header:
    'Score game with full stats (ie. pitches, hits, walks, errors)',
  score_game_full: 'Full game scoring',
  score_game_full_unavailable: "Full scoring isn't available for this game",
  score_game_full_not_scorer:
    'Only people who run a team in this game can score it',
  score_game_scoreboard_header:
    'Operate simplified scoreboard with only bases, balls, strikes, outs, and inning',
  score_game_scoreboard: 'Scoreboard',
  score_game_scoreboard_closed: "This game's scoreboard has closed",
  score_game_scoreboard_not_runner:
    'Only people who run a team in this game can operate the scoreboard',
  score_game_scoreboard_unavailable: 'The scoreboard is unavailable right now',
  score_game_link_header: 'Web Widget Scoreboard Link',
  score_game_copy_link: 'Copy scoreboard link',
  score_game_copied: 'Copied',

  // The scoreboard override dashboard
  override_loading: 'Connecting to the scoreboard…',
  override_away_team: 'Away team name',
  override_home_team: 'Home team name',
  override_away_score: 'Away score',
  override_home_score: 'Home score',
  override_inning: 'Inning',
  override_top: 'Top of the inning',
  override_bottom: 'Bottom of the inning',
  override_first: 'First base',
  override_second: 'Second base',
  override_third: 'Third base',
  override_add_ball: 'Add a ball',
  override_add_strike: 'Add a strike',
  override_add_out: 'Add an out',
  override_clear_balls: 'Clear balls',
  override_clear_strikes: 'Clear strikes',
  override_clear_outs: 'Clear outs',
  override_revert_team: 'Revert team names',
  override_revert_score: 'Revert score',
  override_revert_inning: 'Revert inning',
  override_revert_bases: 'Revert bases',
  override_revert_count: 'Revert count',
  override_pick: 'Pick %s',
  override_close_picker: 'Close the picker',
  override_reset: 'Reset Game State',

  // Dictation
  dictation_button: 'Dictation',
  dictation_status: 'Dictation status',
  dictation_log: 'Dictation log',
  dictation_said: 'Dictated text',
  dictation_play: 'Dictated play',
  dictation_message: 'Dictation message',
  dictation_question: 'Dictation question',
  dictation_connecting: 'Connecting…',
  dictation_listening: 'Listening…',
  dictation_working: 'Working it out…',
  dictation_push: 'Push to talk',
  dictation_ellipsis: '…',
  dictation_dismiss_question: 'Dismiss question',
  dictation_neither: 'Neither — dismiss',
  dictation_record: 'Record',
  dictation_record_nobody: 'Record — nobody out',
  dictation_dismiss_score: 'Dismiss — score nothing',
  dictation_dropped:
    'The connection dropped and that was not scored. Say it again.',
  dictation_left_out: 'Left out: %s',
  dictation_mic_unavailable: 'The microphone is not available: %s',
  dictation_chat: 'Dictation chat',
  dictation_changes_discarded: 'Changes discarded',
  dictation_roster_play: 'That is a play — score it on the scoring screen',
  dictation_roster_other_team: 'Only the %s roster changes here',
  dictation_substitution_separately:
    'Say a substitution separately from a play',

  // Phone streaming
  stream_cameras_title: 'Cameras',
  stream_record_video: 'Record Video',
  stream_prompt_title: 'Name this camera angle',
  stream_prompt_label: 'Camera angle',
  stream_prompt_placeholder: 'i.e., Home, 1st, 3rd, Backstop',
  stream_prompt_suggestion_home: 'Home',
  stream_prompt_suggestion_first: '1st',
  stream_prompt_suggestion_third: '3rd',
  stream_prompt_suggestion_backstop: 'Backstop',
  stream_prompt_cancel: 'Cancel',
  stream_prompt_phone: 'Record with this phone',
  stream_prompt_external: 'Use external camera',
  stream_prompt_too_long: 'A camera name can be at most %s characters.',
  stream_prompt_separator: 'A camera name cannot contain " — ".',
  stream_empty_title: 'No videos',
  stream_empty_can_film:
    'Help bring this game to other fans by live streaming with this phone or external camera.',
  stream_empty_can_request: 'Want to record video for this game?',
  stream_request_access: 'Request access',
  stream_request_sent: 'Request sent',
  stream_limit_person:
    'You already have %s cameras on this game. End one to add another.',
  stream_limit_game:
    'This game already has %s cameras. End one to add another.',
  stream_source_title: 'Stream with your camera',
  stream_source_gopro: 'GoPro',
  stream_source_mevo: 'Mevo',
  stream_source_mevo_studio: 'Mevo Studio',
  stream_source_other: 'Other camera',
  stream_source_other_detail: 'RTMP compatible camera',
  stream_rtmp_title: 'Stream using RTMP',
  stream_rtmp_use_url: 'Use the URL below and enter it into your camera.',
  stream_rtmp_key_blank: 'If your app requests a stream key, leave it blank.',
  stream_rtmp_hevc: 'Your camera must stream in HEVC (H.265).',
  stream_rtmp_return:
    'You may now return to the app and stream to the RTMP URL at any time.',
  stream_rtmp_copied: 'Copied',
  stream_rtmp_revoke: 'Revoke',
  stream_rtmp_done: 'Done',
  stream_revoke_title: 'Revoke this camera?',
  stream_revoke_body:
    'This will revoke the RTMP URL and cancel the stream of %s.',
  stream_pill_idle: 'Press record to start stream',
  stream_pill_connecting: 'Connecting',
  stream_pill_live: 'Broadcasting',
  stream_pill_ended: 'Stream ended',
  stream_record_start: 'Start recording',
  stream_record_stop: 'Stop recording',
  stream_slide_to_stop: 'Slide to Stop',
  stream_close: 'Close camera',
  stream_settings: 'Camera settings',
  stream_flip: 'Flip camera',
  stream_error_permission:
    'TeamStation needs the camera and the microphone to record. Allow them in Settings.',
  stream_error_uploading: '%s is still uploading',
  stream_source_too_slow:
    'That took too long. Check your connection, and see the camera list before trying again.',
  stream_error_failed: 'That did not work: %s',
  stream_camera_unavailable: 'Camera unavailable',
  stream_streaming_only: 'Streaming only: not enough free space to record',
  stream_angle_live: 'Live',
  stream_angle_idle: 'Idle',
  stream_angle_ended: 'Ended',
  stream_angle_more: 'More for %s',
  stream_angle_not_uploaded: 'Recording on %s not uploaded',
  stream_action_copy: 'Copy stream URL',
  stream_action_rotate: 'Rotate key',
  stream_action_end: 'End',
  stream_action_again: 'Tap again: %s',
  stream_row_using_rtmp: 'Using RTMP URL',
  stream_row_using_gopro: 'Using GoPro',
  stream_row_using_mevo: 'Using Mevo',
  stream_row_using_mevo_studio: 'Using Mevo Studio',
  stream_row_waiting: 'Waiting for stream from %s',
  stream_row_streaming: 'Streaming with %s',
  stream_row_other: '%s streaming',
  stream_row_a_phone: 'a phone',
  stream_row_end: 'End',
  stream_row_end_label: 'End %s',
  stream_row_delete: 'Delete',
  stream_row_delete_label: 'Delete %s',
  stream_end_title: 'End this camera?',
  stream_end_body:
    'This ends the stream of %s on the device filming it. Its video is kept.',
  stream_delete_title: 'Delete this video?',
  stream_delete_body:
    'This deletes the video of %s and cannot be undone. Any copy on this phone is deleted too.',
  stream_delete_copy_title: 'Delete the local copy?',
  stream_delete_copy_body:
    'This deletes the copy of %s on this phone and cannot be undone.',
  stream_details_title: 'Video details',
  stream_details_play: 'Play',
  stream_details_delete: 'Delete video',
  stream_details_upload: 'Upload local copy',
  stream_details_delete_copy: 'Delete local copy',
  stream_upload_button: 'Upload Recording',
  stream_upload_caption: 'Provide a higher-quality copy for future viewing.',
  stream_upload_exporting: 'Preparing',
  stream_upload_uploading: 'Uploading',
  stream_upload_processing: 'Processing',
  stream_upload_cancel: 'Cancel',
  stream_upload_retry: 'Retry',
  stream_upload_failed: 'The upload stopped: %s',
  stream_upload_interrupted: 'The upload was interrupted.',

  // Shared
  stream_settings_title: 'Camera',
  stream_settings_done: 'Done',
  stream_settings_audio: 'Audio',
  stream_settings_microphone: 'Microphone',
  stream_settings_lenses: 'Lenses',
  stream_settings_lenses_none: 'This camera has one lens.',
  stream_settings_name: 'Name',
  stream_settings_name_label: 'Camera angle',
  stream_settings_radar: 'Radar',
  stream_settings_radar_intro:
    'Track pitch speed and exit velocity with Tag Sports Radar.',
  stream_settings_radar_on: 'On',
  stream_settings_connected: 'Connected',
  stream_settings_unpair: 'Unpair',
  stream_settings_pair_device: 'Pair Device',
  stream_settings_pair_title: 'Pair Device',
  stream_settings_pair_none: 'No radars in range',
  stream_settings_pair_failed: 'Could not pair %s',
  stream_settings_close: 'Close',
  stream_settings_bluetooth_off: 'Bluetooth is off',
  stream_settings_permission:
    'Allow Bluetooth for TeamStation in Settings to use a radar.',
  stream_settings_mph: 'mph',
  common_done: 'Done',
  common_back: 'Back',
  common_retry: 'Try again',
  common_loading: 'Loading…',
  common_not_found: 'That game is not here',
  common_not_found_detail:
    'It may have been removed, or the link may be for a game you cannot see.',
  common_offline: 'You appear to be offline.',

  // Lineup QR codes, shared with and scanned from GameChanger on the
  // scorekeeper's Set Team Rosters screen.
  lineupQr_shareRow: 'Share or import team lineup',
  lineupQr_shareOwn: 'Share Team Lineup',
  lineupQr_scanOpponent: 'Scan to Import Team Lineup',
  lineupQr_chooserSubtitle: 'Share/Import Lineup',
  lineupQr_generate: 'Generate QR to share lineup',
  lineupQr_scan: 'Scan QR code to import lineup',
  lineupQr_shareSubtitle: 'Share Lineup',
  lineupQr_importSubtitle: 'Import Lineup',
  lineupQr_qrLabel: 'Lineup QR code',
  lineupQr_rosterLoading: 'Loading the team roster…',
  lineupQr_unsupportedWeb:
    'Unsupported on the web version of TeamStation. Please download the app.',
  lineupQr_notLineup: 'That QR code is not a GameChanger lineup.',
  lineupQr_wrongShape: 'That lineup QR code could not be read.',
  lineupQr_empty: 'That lineup QR code has no players.',
  lineupQr_cameraDenied:
    'TeamStation needs the camera to scan a lineup. Allow it in Settings.',
  lineupQr_scanAgain: 'Scan Again',
  lineupQr_cameraUnavailable: 'The camera is not available.',
  lineupQr_refused: 'That lineup cannot be loaded now: %s',
  lineupQr_guestFailed:
    'The scanned players could not be added. Check the connection and scan again.',
  lineupQr_payload: 'QR payload',
  lineupQr_inject: 'Inject',
  lineupQr_camera: 'Camera %s',
  lineupQr_imported_one: 'Imported 1 player',
  lineupQr_imported_other: 'Imported %s players',
  lineupQr_benched: '%s benched',
  tess_ask: 'Ask Tess',
  tess_ask_detail: 'Your AI chatbot',
  tess_heading: 'Ask Tess',
  tess_close: 'Close Tess',
  tess_empty:
    'Ask Tess where to find something in TeamStation, or what to change.',
  tess_thinking: 'Tess is thinking…',
  tess_error: 'Tess could not answer. Please try again.',
  tess_placeholder: 'Ask Tess',
  tess_send: 'Send',
  tess_stop: 'Stop Tess',
  tess_stopped: 'Stopped',
  tess_queued: 'queued',
  tess_queue_send_now: 'Send now',
  tess_queue_remove: 'Remove from queue',
  tess_queue_edit: 'Edit queued message: %s',
  tess_queue_unsure:
    'This message may not have reached Tess. Send it again, or remove it.',
  tess_mic: 'Dictate',
  tess_mic_push: 'Push to talk',
  tess_mic_off: 'Off',
  tess_mic_opening: 'Starting',
  tess_mic_recording: 'Recording',
  tess_mic_transcribing: 'Transcribing',
  tess_attach: 'Attach a file',
  tess_attach_title: 'Attach',
  tess_attach_photo: 'Photo',
  tess_attach_file: 'File',
  tess_attach_remove: 'Remove %s',
  tess_attach_described: '%s (described)',
  tess_attaching: 'Attaching…',
  tess_drop_here: 'Drop files here',
  tess_choose_files: 'Choose files',
  tess_attach_unsupported:
    'Tess reads images, CSV, plain text and Markdown files.',
  tess_attach_too_large: '%s is too large to attach.',
  tess_attach_too_many:
    'A message to Tess takes at most %s files. Send these first, or remove one.',
  tess_attach_unreadable: 'That file could not be read.',
  tess_attach_failed: 'The file could not be opened. Please try again.',
  tess_hold_confirm: 'Hold to confirm',
  tess_hold_hint: 'Hold for two seconds to run every change above.',
  tess_confirming: 'Confirming…',
  tess_change_done: 'Done',
  tess_change_failed: 'Failed',
  tess_change_expired: 'Expired',
  tess_change_check: 'Check',
  tess_change_unknown:
    'A change marked Check may or may not have run. Check whether it happened before asking Tess again.',
  tess_change_column: 'Change',
  tess_change_status: 'Status',
  tess_change_requests: 'Request details',
  tess_change_expand: 'Expand',
  tess_change_expand_label: 'Expand the changes over the page',
  tess_rich_title: 'A table or chart from Tess',
  tess_rich_open: 'Open full screen',
  tess_rich_close: 'Close',
  tess_way: 'Go to %s',
  tess_resize: 'Resize Tess',
  tess_thinking_live: 'Thinking',
  tess_thought_for: 'Thought for %ss',
  tess_step_running: '%s, in progress',
  tess_step_done: '%s, done',
  tess_step_failed: '%s, failed'
}

export type StringKey = keyof typeof enUs
